/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Fetch a Markdown file from a public GitHub repository's raw content URL
 * with a small in-memory cache. Used to render the live `humphreytheodore/person`
 * protocol repo on dotperson.org without bundling its contents at build time.
 */

const RAW_BASE = "https://raw.githubusercontent.com";
const PROTOCOL_REPO = "humphreytheodore/person";
const PROTOCOL_BRANCH = "main";

type CacheEntry = {
  content: string;
  fetchedAt: number;
};

const cache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch a Markdown file from the protocol repo.
 *
 * @param relativePath  Path within the repo (e.g. "README.md", "schema/README.md").
 * @returns             The Markdown source string.
 */
export async function fetchProtocolDoc(relativePath: string): Promise<string> {
  const url = `${RAW_BASE}/${PROTOCOL_REPO}/${PROTOCOL_BRANCH}/${relativePath}`;

  const cached = cache.get(url);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.content;
  }

  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) {
    throw new Error(`Failed to fetch ${relativePath} from ${PROTOCOL_REPO} — HTTP ${res.status}`);
  }
  const content = await res.text();
  cache.set(url, { content, fetchedAt: Date.now() });
  return content;
}

/**
 * Build a github.com URL for the same file (for "Edit on GitHub" links).
 */
export function githubBlobUrl(relativePath: string): string {
  return `https://github.com/${PROTOCOL_REPO}/blob/${PROTOCOL_BRANCH}/${relativePath}`;
}

/**
 * Build a github.com URL for the repo home (for "View on GitHub" links).
 */
export function githubRepoUrl(): string {
  return `https://github.com/${PROTOCOL_REPO}`;
}
